import { isoAttr } from "../../lib/datetime.ts";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBadge } from "../../components/index.ts";
import type { AuditChain } from "./use-integrity.ts";
import { AuditChainDetailView } from "./AuditChainDetail.tsx";
import {
  verificationLevelStatus,
  verificationLevelLabel,
  verificationLevelTitle,
  chainStatusLevel,
  chainStatusLabel,
  chainStatusReasonLabel,
  firstFailureView,
  headPositionText,
  exportLagView,
  chainEventsHref,
} from "./vocab.ts";

/**
 * Audit chains (task 11.9; design 12.2 Integrity row; 12.3 drill-down; 12.4 semantic table).
 *
 * This is the heart of the screen. It renders one row per audit chain, and EVERY row ALWAYS shows
 * the four members the contract guarantees are present (design 12.2) — there is no code path that
 * hides any of them:
 *
 *   1. VERIFICATION LEVEL — `StatusBadge` from {@link verificationLevelStatus}. The `pending`
 *      sentinel (level undetermined) maps to the grey `unknown` level: "unverifiable is never green"
 *      (design 12.3), so a not-yet-verified chain can never read as confirmed. A low determined
 *      level (received-only / batch-level) is a warn, also not green; only entry-level is healthy.
 *   2. HEAD POSITION — the batch/entry head, or an honest "No batches yet" before the first batch.
 *   3. EXPORT LAG — always shown and labelled "lag"; a growing lag is flagged as a warning signal.
 *      When the server could not measure it, that is shown as "Unknown", never hidden or zeroed.
 *   4. FIRST FAILURE — the `none` sentinel (no failure; the chain is clean so far) renders as a calm
 *      "None" healthy badge, which is DISTINCT from the `pending` level: `none` = determined-clean,
 *      `pending` = undetermined. A real failure shows its reason, batch and time.
 *
 * The row's own status (`ok | failed | unverifiable | stale`) leads the row; `unverifiable` and
 * `stale` are grey, `failed` is an error, only `ok` is green (design 12.3). A row expands in place to
 * the chain detail (`/integrity/audit-chains/{id}` via {@link AuditChainDetailView}); the row toggle
 * is a real `<button>` with `aria-expanded`, and the chain also offers a direct drill to its events.
 *
 * The table is hand-rolled (not the shared `DenseTable`) because it needs expandable detail rows,
 * which `DenseTable` does not model; it keeps the same semantic shape — a real `<table>` with a
 * `<caption>`, `<th scope="col">` headers and a `<th scope="row">` per chain — so it is just as
 * accessible.
 */

export interface AuditChainsTableProps {
  readonly chains: readonly AuditChain[];
}

export function AuditChainsTable(props: AuditChainsTableProps): ReactNode {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (props.chains.length === 0) {
    return (
      <section className="itg-section" aria-label="Audit chains">
        <div className="itg-section__head">
          <h2 className="itg-section__title">Audit chains</h2>
        </div>
        <div className="itg-empty">
          No audit chains have been reported. Check that sources are exporting audit batches.
        </div>
      </section>
    );
  }

  return (
    <section className="itg-section" aria-label="Audit chains">
      <div className="itg-section__head">
        <h2 className="itg-section__title">Audit chains</h2>
        <p className="itg-section__note">
          Every chain always shows its verification level, head position, export lag and first
          failure. A pending level is never shown as verified.
        </p>
      </div>
      <div className="itg-chains__scroll">
        <table className="data-table data-table--dense" data-sticky="true">
          <caption className="sr-only">
            Audit chains with verification level, head position, export lag and first failure
          </caption>
          <thead>
            <tr>
              <th scope="col">Chain</th>
              <th scope="col">Status</th>
              <th scope="col">Verification level</th>
              <th scope="col">Head position</th>
              <th scope="col">Export lag</th>
              <th scope="col">First failure</th>
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {props.chains.map((chain) => (
              <ChainRow
                key={chain.id}
                chain={chain}
                expanded={expandedId === chain.id}
                onToggle={() =>
                  setExpandedId((current) => (current === chain.id ? null : chain.id))
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ChainRow(props: {
  chain: AuditChain;
  expanded: boolean;
  onToggle: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const { chain } = props;
  const level = chain.verification_level;
  const failure = firstFailureView(chain.first_failure);
  const lag = exportLagView(chain.export_lag);
  const statusReason = chainStatusReasonLabel(chain.status_reason);
  const detailId = `chain-detail-${chain.id}`;

  return (
    <>
      <tr data-expanded={props.expanded ? "true" : undefined}>
        {/* Chain id — the expand toggle, a real button with aria-expanded. */}
        <th scope="row" data-align="left">
          <button
            type="button"
            className="itg-chain-toggle"
            aria-expanded={props.expanded}
            aria-controls={detailId}
            onClick={props.onToggle}
          >
            <span className="itg-chain-toggle__caret" aria-hidden="true">
              {props.expanded ? "▾" : "▸"}
            </span>
            <span className="itg-chain-toggle__id">{chain.instance_id}</span>
          </button>
        </th>

        {/* 0. Row status — unverifiable/stale grey, failed error, only ok green. */}
        <td data-align="left">
          <StatusBadge
            level={chainStatusLevel(chain.status)}
            label={chainStatusLabel(chain.status)}
            title={
              statusReason === null
                ? `Chain status: ${chainStatusLabel(chain.status).toLowerCase()}.`
                : `Chain status: ${chainStatusLabel(
                    chain.status,
                  ).toLowerCase()} — ${statusReason.toLowerCase()}.`
            }
            size="sm"
          />
        </td>

        {/* 1. Verification level — ALWAYS present. `pending` is grey-not-green. */}
        <td data-align="left">
          <StatusBadge
            level={verificationLevelStatus(level)}
            label={verificationLevelLabel(level)}
            title={verificationLevelTitle(level)}
            size="sm"
          />
        </td>

        {/* 2. Head position — ALWAYS present (honest "No batches yet" before the first batch). */}
        <td data-align="left">
          <span className="itg-num">{headPositionText(chain.head)}</span>
        </td>

        {/* 3. Export lag — ALWAYS present and labelled; a growing lag is a warning signal. */}
        <td data-align="left">
          <span className="itg-lag" data-warning={lag.isWarning ? "true" : undefined}>
            <span className="itg-lag__label">lag</span>{" "}
            <span className="itg-num">{lag.text}</span>
          </span>
        </td>

        {/* 4. First failure — ALWAYS present. `none` is a calm "None", distinct from pending. */}
        <td data-align="left">
          {failure.kind === "none" ? (
            <StatusBadge
              level="healthy"
              label="None"
              title="No first failure: the chain is clean so far (distinct from a pending verification level)."
              size="sm"
            />
          ) : (
            <span className="itg-reason" title={`First failure detected ${isoAttr(failure.detectedAtMs)}`}>
              {failure.reasonLabel}
              {failure.batchSeq !== null && ` · #${failure.batchSeq}`}
            </span>
          )}
        </td>

        {/* Direct drill to the chain's events (in addition to the expand-to-detail). */}
        <td data-align="right">
          <button
            type="button"
            className="itg-link"
            onClick={() => navigate(chainEventsHref(chain))}
            aria-label={`Open the audit events for chain ${chain.instance_id}`}
          >
            Events
          </button>
        </td>
      </tr>

      {props.expanded && (
        <tr className="itg-chain-detail-row">
          <td colSpan={7} id={detailId}>
            <AuditChainDetailView chainId={chain.id} />
          </td>
        </tr>
      )}
    </>
  );
}
